import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Guitar, Music, Mic2, MapPin, Mail, Clock, Menu, X, Smartphone, ClipboardList, Download, Copy, Check } from "lucide-react";

// Assets
const LOGO_MAIN = "/logos/logo-rnp.png";
const LOGO_FESTIVAL = "/logos/logo-gbf.png";
const HERO_BG = "https://images.unsplash.com/photo-1645729043983-6d5e6ccbc617?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxwdW5rJTIwcm9jayUyMGNvbmNlcnQlMjBjcm93ZCUyMHNpbGhvdWV0dGV8ZW58MHx8fHwxNzcwMjE2MjU3fDA&ixlib=rb-4.1.0&q=85";
const MUSICIAN_2 = "https://images.unsplash.com/photo-1565022449228-da5a3f947ed3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxpbmslMjBzcGxhdHRlciUyMHJvY2slMjBndWl0YXJpc3QlMjBibGFjayUyMGFuZCUyMHdoaXRlfGVufDB8fHx8MTc3MDIxNjI3NXww&ixlib=rb-4.1.0&q=85";
const CROWD = "https://images.unsplash.com/photo-1585230699768-a31a4d76e48f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxwdW5rJTIwcm9jayUyMGNvbmNlcnQlMjBjcm93ZCUyMHNpbGhvdWV0dGV8ZW58MHx8fHwxNzcwMjE2MjU3fDA&ixlib=rb-4.1.0&q=85";

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-rock-black/95 backdrop-blur-sm border-b-2 border-rock-red" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <a href="#hero" onClick={() => scrollToSection("hero")} className="flex items-center gap-3">
            <img src={LOGO_MAIN} alt="Rock 'n' Piné" className="h-12 w-auto" />
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection("chi-siamo")}
              className="font-heading uppercase text-white hover:text-rock-red transition-colors tracking-wider"
            >
              Chi Siamo
            </button>
            <button
              onClick={() => scrollToSection("dove-siamo")}
              className="font-heading uppercase text-white hover:text-rock-red transition-colors tracking-wider"
            >
              Dove Siamo
            </button>
            <button
              onClick={() => scrollToSection("festival")}
              className="font-heading uppercase text-white hover:text-rock-red transition-colors tracking-wider"
            >
              Festival
            </button>
            <button
              onClick={() => scrollToSection("app")}
              className="font-heading uppercase text-white hover:text-rock-yellow transition-colors tracking-wider"
            >
              App
            </button>
            <button
              onClick={() => scrollToSection("sala-prove")}
              className="font-heading uppercase text-white hover:text-rock-pink transition-colors tracking-wider"
            >
              Sala Prove
            </button>
            <button
              onClick={() => scrollToSection("affitto-sala")}
              className="font-heading uppercase text-white hover:text-rock-yellow transition-colors tracking-wider"
            >
              Affitto Sala
            </button>
            <button
              onClick={() => scrollToSection("contatti")}
              className="font-heading uppercase text-white hover:text-rock-red transition-colors tracking-wider"
            >
              Contatti
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-rock-border pt-4">
            <div className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection("chi-siamo")}
                className="font-heading uppercase text-white hover:text-rock-red transition-colors tracking-wider text-left"
              >
                Chi Siamo
              </button>
              <button
                onClick={() => scrollToSection("dove-siamo")}
                className="font-heading uppercase text-white hover:text-rock-red transition-colors tracking-wider text-left"
              >
                Dove Siamo
              </button>
              <button
                onClick={() => scrollToSection("festival")}
                className="font-heading uppercase text-white hover:text-rock-red transition-colors tracking-wider text-left"
              >
                Festival
              </button>
              <button
                onClick={() => scrollToSection("app")}
                className="font-heading uppercase text-white hover:text-rock-yellow transition-colors tracking-wider text-left"
              >
                App
              </button>
              <button
                onClick={() => scrollToSection("sala-prove")}
                className="font-heading uppercase text-white hover:text-rock-pink transition-colors tracking-wider text-left"
              >
                Sala Prove
              </button>
              <button
                onClick={() => scrollToSection("affitto-sala")}
                className="font-heading uppercase text-white hover:text-rock-yellow transition-colors tracking-wider text-left"
              >
                Affitto Sala
              </button>
              <button
                onClick={() => scrollToSection("contatti")}
                className="font-heading uppercase text-white hover:text-rock-red transition-colors tracking-wider text-left"
              >
                Contatti
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-black/75" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <img
          src={LOGO_MAIN}
          alt="Rock 'n' Piné"
          className="mx-auto w-64 md:w-80 lg:w-96 mb-8 animate-fade-up"
        />
        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter text-white mb-4 transform -rotate-1">
          LOUD. LOCAL. <span className="text-rock-red">LIVE.</span>
        </h1>
        <p className="font-body text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto">
          Associazione di Promozione Sociale a Baselga di Piné
        </p>

        <div className="flex flex-col sm:flex-row gap-6 justify-center">
          <button
            data-testid="cta-festival"
            onClick={() => scrollToSection("festival")}
            className="rock-btn-primary animate-pulse-glow"
          >
            Gang Band Festival 2026
          </button>
          <button
            data-testid="cta-sala-prove"
            onClick={() => scrollToSection("sala-prove")}
            className="rock-btn-secondary"
          >
            Prenota Sala Prove
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-white rounded-none flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white rounded-none animate-pulse" />
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="chi-siamo" data-testid="about-section" className="py-24 bg-rock-zinc">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="animate-slide-left">
            <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-2">
              CHI <span className="text-rock-red">SIAMO</span>
            </h2>
            <div className="h-1 w-32 bg-rock-red mb-8" />

            <p className="font-body text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
              <strong className="text-white">Rock &apos;n&apos; Piné</strong> è un&apos;associazione culturale nata dalla passione
              per la musica rock e dalla voglia di creare uno spazio dove musicisti e appassionati possano
              incontrarsi, suonare e condividere la loro passione.
            </p>
            <p className="font-body text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
              Con sede a <strong className="text-rock-pink">Baselga di Piné</strong>, nel cuore del Trentino,
              gestiamo una sala prove professionale e organizziamo eventi musicali, tra cui il famoso
              <strong className="text-rock-red"> Gang Band Festival</strong>.
            </p>
            <p className="font-body text-lg md:text-xl text-gray-300 leading-relaxed">
              La nostra missione? Tenere vivo il rock nella nostra comunità e dare spazio ai talenti locali
              e non solo.
            </p>
          </div>

          {/* Image */}
          <div className="animate-slide-right">
            <div className="relative">
              <img
                src="/direttivo.jpg"
                alt="Direttivo Rock 'n' Piné"
                className="tilted-image w-full grayscale contrast-125 hover:grayscale-0 transition-all duration-500"
              />
              <div className="absolute -bottom-4 -right-4 bg-rock-red p-4 border-2 border-white">
                <Music className="text-white" size={32} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Rehearsal Room Section
const RehearsalRoomSection = () => {
  const equipment = [
    { icon: Guitar, name: "Amplificatori", desc: "Ampli basso, Fender, casse passive" },
    { icon: Music, name: "Batteria", desc: "Kit completo professionale" },
    { icon: Mic2, name: "Impianto PA", desc: "Sistema audio professionale" },
    { icon: Clock, name: "Orari Flessibili", desc: "7 giorni su 7" },
  ];

  return (
    <section id="sala-prove" data-testid="rehearsal-section" className="py-24 bg-rock-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
        <img src={MUSICIAN_2} alt="" className="w-full h-full object-cover grayscale" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-2">
            SALA <span className="text-rock-pink">PROVE</span>
          </h2>
          <div className="h-1 w-32 bg-rock-pink mx-auto mb-8" />
          <p className="font-body text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            Uno spazio attrezzato dove la tua musica può prendere vita. Strumentazione professionale,
            acustica curata e atmosfera rock.
          </p>
        </div>

        {/* Equipment Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {equipment.map((item, index) => (
            <div
              key={index}
              data-testid={`equipment-card-${index}`}
              className="bg-rock-zinc border-2 border-rock-border p-6 relative group hover:border-rock-pink transition-colors"
            >
              {/* Tape effect */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-12 h-4 bg-rock-yellow opacity-90" />

              <item.icon className="text-rock-pink mb-4" size={40} />
              <h3 className="font-heading text-xl font-bold uppercase text-white mb-2">{item.name}</h3>
              <p className="font-body text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-rock-zinc border-2 border-rock-pink p-8">
            <h3 className="font-heading text-2xl font-bold uppercase text-rock-pink mb-4">Abbonamenti</h3>
            <ul className="font-body text-gray-300 space-y-4">
              <li className="flex justify-between items-center border-b border-rock-border pb-4">
                <span className="text-lg">Abbonamento Semestrale</span>
                <span className="text-rock-pink font-heading uppercase text-sm">Contattaci</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="text-lg">Abbonamento Annuale</span>
                <span className="text-rock-pink font-heading uppercase text-sm">Contattaci</span>
              </li>
            </ul>
          </div>

          <div className="bg-rock-zinc border-2 border-rock-pink p-2">
            <h3 className="font-heading text-2xl font-bold uppercase text-rock-pink mb-4">Come Prenotare</h3>
            <p className="font-body text-gray-300 mb-4">
              Contattaci via email per verificare la disponibilità e prenotare la tua sessione.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-300">
                <Mail className="text-rock-pink" size={20} />
                <span>associazione.rocknpine@gmail.com</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <MapPin className="text-rock-pink" size={20} />
                <span>Via del Bedolé 8, Baselga di Piné, TN</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Affitto Sala Polivalente: richiesta con il modulo Google oppure con il PDF in public/docs
const RENTAL_FORM_URL = "https://forms.gle/ZHS12bbkYjFCDQZDA";
const RENTAL_PDF = "/docs/affitto-sala-polivalente.pdf";
const RENTAL_IBAN = "IT91 V0817 83433 00000 23019019";
const RENTAL_EMAIL = "associazione.rocknpine@gmail.com";
const RENTAL_BILLING = [
  { label: "Denominazione", value: "Rock 'n Piné APS" },
  { label: "Sede legale", value: "Via C. Battisti 50, 38042 Baselga di Piné (TN)" },
  { label: "Codice fiscale", value: "02300050222" },
  { label: "Partita IVA", value: "02300050222" },
  { label: "Codice destinatario SDI", value: "M5UXCR1" },
];

// Rental Section
const RentalSection = () => {
  const [copied, setCopied] = useState(false);

  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(RENTAL_IBAN.replace(/\s/g, ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard non disponibile: l'IBAN resta selezionabile a mano
    }
  };

  const steps = [
    {
      title: "Compila il modulo",
      desc: (
        <>
          Compilalo online, oppure scarica il PDF, compilalo e invialo via email all&apos;indirizzo{" "}
          <a href={`mailto:${RENTAL_EMAIL}`} className="text-white underline break-words hover:text-rock-yellow">
            {RENTAL_EMAIL}
          </a>
        </>
      ),
      action: (
        <div className="flex flex-wrap gap-2">
          <a
            href={RENTAL_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rental-form-btn"
            className="inline-flex items-center gap-2 font-heading uppercase text-sm text-white border-2 border-white px-3 py-1 hover:bg-white hover:text-black transition-all"
          >
            <ClipboardList size={16} />
            Modulo online
          </a>
          <a
            href={RENTAL_PDF}
            download
            data-testid="rental-pdf-btn"
            className="inline-flex items-center gap-2 font-heading uppercase text-sm text-white border-2 border-white px-3 py-1 hover:bg-white hover:text-black transition-all"
          >
            <Download size={16} />
            Modulo PDF
          </a>
        </div>
      ),
    },
    {
      title: "Attendi la conferma",
      desc: "Ti contattiamo per confermare la disponibilità. Hai domande? Scrivici.",
      action: (
        <a
          href={`mailto:${RENTAL_EMAIL}?subject=${encodeURIComponent("Affitto Sala Polivalente")}`}
          className="inline-flex items-center gap-2 font-heading uppercase text-sm text-white border-2 border-white px-3 py-1 hover:bg-white hover:text-black transition-all"
        >
          <Mail size={16} />
          Scrivici
        </a>
      ),
    },
    {
      title: "Paga con bonifico",
      desc: "Dopo la conferma effettua il bonifico con la causale indicata qui sotto.",
    },
  ];

  return (
    <section id="affitto-sala" data-testid="rental-section" className="py-24 bg-rock-zinc relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-2">
            AFFITTO SALA <span className="text-rock-yellow">POLIVALENTE</span>
          </h2>
          <div className="h-1 w-32 bg-rock-yellow mx-auto mb-8" />
          <p className="font-body text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-6">
            La sala polivalente dell&apos;associazione si può affittare per eventi, incontri e attività.
            Ecco come prenotarla.
          </p>
          <div className="flex items-center justify-center gap-3 text-gray-300">
            <MapPin className="text-rock-yellow flex-shrink-0" size={20} />
            <span className="font-body">Via del Bedolé 8, Baselga di Piné (TN)</span>
          </div>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {steps.map((step, index) => (
            <div
              key={step.title}
              data-testid={`rental-step-${index}`}
              className="bg-rock-black border-2 border-rock-border p-6 relative flex flex-col"
            >
              {/* Tape effect */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-12 h-4 bg-rock-yellow opacity-90" />

              <div className="font-heading text-5xl font-black text-rock-yellow mb-4">0{index + 1}</div>
              <h3 className="font-heading text-xl font-bold uppercase text-white mb-2">{step.title}</h3>
              <p className="font-body text-gray-400">{step.desc}</p>
              {step.action && <div className="mt-auto pt-4">{step.action}</div>}
            </div>
          ))}
        </div>

        {/* Payment + billing data */}
        <div className="grid md:grid-cols-2 gap-8">
          <div data-testid="rental-payment" className="bg-rock-black border-2 border-rock-yellow p-6 md:p-8">
            <h3 className="font-heading text-2xl font-bold uppercase text-rock-yellow mb-6">Pagamento con bonifico</h3>
            <dl className="font-body space-y-5">
              <div>
                <dt className="text-sm uppercase tracking-wider text-gray-500 mb-1">Intestatario</dt>
                <dd className="text-lg text-white">Rock &apos;n Piné APS</dd>
              </div>
              <div>
                <dt className="text-sm uppercase tracking-wider text-gray-500 mb-1">IBAN</dt>
                <dd className="flex flex-wrap items-center gap-3">
                  <span className="text-lg md:text-xl font-bold text-white tracking-wide">{RENTAL_IBAN}</span>
                  <button
                    type="button"
                    onClick={copyIban}
                    data-testid="copy-iban-btn"
                    className="inline-flex items-center gap-2 font-heading uppercase text-sm text-white border-2 border-white px-3 py-1 hover:bg-white hover:text-black transition-all"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? "Copiato" : "Copia"}
                  </button>
                </dd>
              </div>
              <div>
                <dt className="text-sm uppercase tracking-wider text-gray-500 mb-1">Causale</dt>
                <dd className="text-white">Affitto Sala Polivalente &ndash; nome e cognome / ente &ndash; data di utilizzo</dd>
              </div>
            </dl>
          </div>

          <div data-testid="rental-billing" className="bg-rock-black border-2 border-rock-border p-6 md:p-8">
            <h3 className="font-heading text-2xl font-bold uppercase text-white mb-6">Dati dell&apos;associazione</h3>
            <dl className="font-body space-y-5">
              {RENTAL_BILLING.map((item) => (
                <div key={item.label}>
                  <dt className="text-sm uppercase tracking-wider text-gray-500 mb-1">{item.label}</dt>
                  <dd className="text-white">{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sponsor del Festival, divisi per livello
const SPONSOR_TIERS = [
  {
    tier: "Platinum",
    titleClass: "text-gray-100",
    barClass: "bg-gray-100",
    logoClass: "h-20 md:h-28",
    sponsors: [
      { name: "Cassa Rurale Alta Valsugana", logo: "/sponsors/crav.png" },
      { name: "Bonelli Ponteggi", logo: "/sponsors/bonelli.png" },
    ],
  },
  {
    tier: "Gold",
    titleClass: "text-rock-yellow",
    barClass: "bg-rock-yellow",
    logoClass: "h-16 md:h-20",
    sponsors: [
      { name: "BerryPlant", logo: "/sponsors/berryplant.png" },
      { name: "Leveghi", logo: "/sponsors/leveghi.png" },
      { name: "Vittoria Assicurazioni - Mattivi Marco", logo: "/sponsors/vittoria-mattivi.png" },
      { name: "Tessadri Angelo", logo: "/sponsors/tessadri.png" },
    ],
  },
  {
    tier: "Silver",
    titleClass: "text-gray-400",
    barClass: "bg-gray-400",
    logoClass: "h-12 md:h-16",
    sponsors: [
      { name: "Bortolotti Pitture", logo: "/sponsors/bortolotti.png" },
      { name: "Bottega della Musica", logo: "/sponsors/bottega-della-musica.png" },
      { name: "La Casa della Pasta", logo: "/sponsors/casa-della-pasta.png" },
      { name: "Goccia Hair Studyo", logo: "/sponsors/goccia-hair.png" },
    ],
  },
  {
    tier: "Bronze",
    titleClass: "text-amber-700",
    barClass: "bg-amber-700",
    logoClass: "h-14 md:h-16",
    sponsors: [
      { name: "Alpes Sport", logo: "/sponsors/alles.png" },
      { name: "Alpino Friends Club", logo: "/sponsors/alpino.png" },
      { name: "CO.IM.P. Impianti Elettrici", logo: "/sponsors/coimp.png" },
      { name: "CreaSogni Cartoleria", logo: "/sponsors/creasogni.png" },
      { name: "Fagitana", logo: "/sponsors/fagitana.png" },
      { name: "Gocce d'Oro", logo: "/sponsors/gocce-doro.png" },
      { name: "Linea Color", logo: "/sponsors/lineacolor.png" },
      { name: "Oss Emer", logo: "/sponsors/oss-emer.png" },
      { name: "Spazzacamino Valentino Dalvit", logo: "/sponsors/tino-spazzacamino.png" },
    ],
  },
];

// Festival Section
const FestivalSection = () => {
  return (
    <section id="festival" data-testid="festival-section" className="py-24 bg-rock-zinc relative overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url(${CROWD})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-rock-black via-transparent to-rock-black" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <img
            src={LOGO_FESTIVAL}
            alt="Gang Band Festival"
            className="mx-auto w-80 md:w-[500px] lg:w-[600px] mb-8"
          />
          <div className="inline-block bg-rock-red px-8 py-4 border-4 border-white transform -rotate-1 mb-8">
            <h3 className="font-heading text-3xl md:text-5xl font-black uppercase text-white tracking-tighter">
              24 - 28 GIUGNO 2026
            </h3>
          </div>
          <p className="font-body text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            5 giorni di orgia musicale nel cuore delle Dolomiti. Band locali e nazionali, cibo, birra e
            tanta energia!
          </p>
        </div>

        {/* Main Days - Highlighted */}
        <div className="mb-12">
          <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase text-rock-red text-center mb-8">
            Headliners
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Venerdì 26 */}
            <div
              data-testid="festival-day-friday"
              className="bg-rock-black border-4 border-rock-red p-6 text-center relative group hover:bg-rock-red/10 transition-all duration-300"
            >
              <div className="font-heading text-4xl md:text-5xl font-black text-rock-red mb-2">
                26 GIU
              </div>
              <div className="font-body text-lg text-gray-400 uppercase mb-4">
                Venerdì
              </div>
              <div className="space-y-4 mb-4">
                <div className="flex justify-center">
                  <img src="/logos/rebel rootz.png" alt="Rebel Rootz" className="h-[117px] md:h-[146px] w-auto object-contain" />
                </div>
                <div className="flex justify-center">
                  <img src="/logos/tla.png" alt="TLA" className="h-[51px] md:h-[72px] w-auto object-contain" />
                </div>
                <div className="flex justify-center">
                  <img src="/logos/braska.png" alt="Braska" className="h-[82px] md:h-[113px] w-auto object-contain" />
                </div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">Achille 690</div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">Electric Confidence</div>
              </div>
            </div>

            {/* Sabato 27 */}
            <div
              data-testid="festival-day-saturday"
              className="bg-rock-black border-4 border-rock-red p-6 text-center relative group hover:bg-rock-red/10 transition-all duration-300"
            >
              <div className="font-heading text-4xl md:text-5xl font-black text-rock-red mb-2">
                27 GIU
              </div>
              <div className="font-body text-lg text-gray-400 uppercase mb-4">
                Sabato
              </div>
              <div className="space-y-4 mb-4">
                <div className="flex justify-center">
                  <img src="/logos/rhapsody.png" alt="Rhapsody of Fire" className="h-20 md:h-[100px] w-auto object-contain" />
                </div>
                <div className="flex justify-center">
                  <img src="/logos/trick or treat new logo.png" alt="Trick or Treat" className="h-12 md:h-16 w-auto object-contain" />
                </div>
                <div className="flex justify-center">
                  <img src="/logos/brutti di fosco.png" alt="Brutti di Fosco" className="h-[3.6rem] md:h-[5rem] w-auto object-contain" />
                </div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">Bevermeister</div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">Back on Track</div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">Tooth</div>
              </div>
              <div className="mt-4 pt-4 border-t border-rock-border">

                <a
                  href="https://oooh.events/evento/gang-band-festival-2026-biglietti/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-rock-red text-white font-heading uppercase px-6 py-2 border-2 border-white hover:bg-white hover:text-black transition-all"
                  data-testid="buy-tickets-btn"
                >
                  Acquista Biglietti
                </a>
              </div>
            </div>

            {/* Domenica 28 */}
            <div
              data-testid="festival-day-sunday"
              className="bg-rock-black border-4 border-rock-red p-6 text-center relative group hover:bg-rock-red/10 transition-all duration-300"
            >
              <div className="font-heading text-4xl md:text-5xl font-black text-rock-red mb-2">
                28 GIU
              </div>
              <div className="font-body text-lg text-gray-400 uppercase mb-4">
                Domenica
              </div>
              <div className="space-y-4 mb-4">
                <div className="flex justify-center">
                  <img src="/logos/logo killbilly's.png" alt="The Killbilly's" className="h-[69px] md:h-[86px] w-auto object-contain" />
                </div>
                <div className="flex justify-center">
                  <img src="/logos/swinging pigs.png" alt="Swinging Pigs" className="h-20 md:h-28 w-auto object-contain" />
                </div>
                <div className="flex justify-center">
                  <img src="/logos/one day revolution.png" alt="One Day Revolution" className="h-[3.6rem] md:h-[5rem] w-auto object-contain" />
                </div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">The Proners</div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">Half Faces</div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">Croaking Frogs</div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">Rockglider</div>
                <div className="font-heading text-lg md:text-xl uppercase text-white">John Lou &amp; The Mass Break</div>
              </div>
            </div>
          </div>
        </div>

        {/* Warm Up Days - Secondary */}
        <div className="mb-12">
          <h3 className="font-heading text-xl font-bold uppercase text-gray-500 text-center mb-6">
            Warm Up Days
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <div
              data-testid="festival-warmup-wed"
              className="bg-rock-zinc/50 border border-rock-border px-6 py-4 text-center max-w-xs"
            >
              <div className="font-heading text-xl text-gray-400">24 GIU</div>
              <div className="font-body text-sm text-gray-500 uppercase">Mercoledì</div>
              <div className="font-heading text-rock-red text-base uppercase mt-2">Serata Folk &amp; Country &ndash; 20:30</div>
              <div className="font-body text-xs text-gray-500 mt-1">Piazzale Costalta &ndash; Baselga di Piné</div>
              <div className="font-body text-gray-300 text-sm mt-3">Gruppo Bandistico Folk Pinetano</div>
              <div className="font-body text-gray-300 text-sm mt-1">Rais Pinaitre</div>
              <div className="font-body text-gray-300 text-sm mt-1">Let&apos;s Go (Country Dance)</div>
            </div>
            <div
              data-testid="festival-warmup-thu"
              className="bg-rock-zinc/50 border border-rock-border px-6 py-4 text-center max-w-xs"
            >
              <div className="font-heading text-xl text-gray-400">25 GIU</div>
              <div className="font-body text-sm text-gray-500 uppercase">Giovedì</div>
              <div className="font-heading text-rock-red text-base uppercase mt-2">Rassegna Cori &ndash; 20:30</div>
              <div className="font-body text-xs text-gray-500 mt-1">Centro Congressi Piné 1000 &ndash; Baselga</div>
              <div className="font-body text-gray-300 text-sm mt-3">Coro Incontro di Voci</div>
              <div className="font-body text-gray-300 text-sm mt-1">Coro Abete Rosso</div>
              <div className="font-body text-gray-300 text-sm mt-1">Coro Costalta</div>
            </div>
          </div>
        </div>

        {/* Location Info */}
        <div id="dove-siamo" className="max-w-3xl mx-auto">
          <div className="bg-rock-black/80 border-2 border-white p-6">
            <div className="flex items-center justify-center gap-4 mb-6">
              <MapPin className="text-rock-red" size={24} />
              <span className="font-heading text-xl uppercase text-white">Via del Bedolé 8, Baselga di Piné (TN)</span>
            </div>
            <div className="w-full aspect-video border-2 border-rock-border">
              <iframe
                title="Sede Rocknpine"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.5!2d11.2289154!3d46.114095!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478277d73ca11d77%3A0xb0f7a84b0e99de72!2sSede%20Rocknpine%20(Ex%20colonie%20mantovane)!5e0!3m2!1sit!2sit!4v1713500000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        {/* Sponsor */}
        <div data-testid="festival-sponsors" className="mt-16">
          <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase text-white text-center mb-2">
            I nostri <span className="text-rock-red">Sponsor</span>
          </h3>
          <div className="h-1 w-24 bg-rock-red mx-auto mb-10" />
          {SPONSOR_TIERS.map((tier) => (
            <div key={tier.tier} data-testid={`sponsor-tier-${tier.tier.toLowerCase()}`} className="mb-10">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className={`h-px w-12 md:w-24 ${tier.barClass}`} />
                <h4 className={`font-heading text-xl md:text-2xl font-bold uppercase tracking-widest ${tier.titleClass}`}>
                  {tier.tier}
                </h4>
                <div className={`h-px w-12 md:w-24 ${tier.barClass}`} />
              </div>
              <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6">
                {tier.sponsors.map((sponsor) => (
                  <div
                    key={sponsor.name}
                    className="bg-white p-3 md:p-4 flex items-center justify-center"
                    title={sponsor.name}
                  >
                    <img
                      src={sponsor.logo}
                      alt={sponsor.name}
                      className={`${tier.logoClass} w-auto max-w-[200px] md:max-w-[260px] object-contain`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// App Section
const AppSection = () => {
  return (
    <section id="app" data-testid="app-section" className="py-24 bg-rock-black relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-2">
            L&apos;<span className="text-rock-yellow">APP</span>
          </h2>
          <div className="h-1 w-32 bg-rock-yellow mx-auto mb-8" />
          <p className="font-body text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
            Salta la fila al bar! Con l&apos;app ufficiale del Gang Band Festival puoi prenotare le tue
            consumazioni direttamente dal telefono.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-rock-zinc border-4 border-rock-yellow p-8 md:p-12 relative">
            {/* Tape effect */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-16 h-5 bg-rock-red opacity-90" />

            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="bg-rock-yellow p-6 border-4 border-white">
                  <Smartphone className="text-rock-black" size={64} />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase text-rock-yellow mb-3">
                  Prenota le consumazioni
                </h3>
                <p className="font-body text-gray-300 mb-6">
                  Ordina cibo e bevande dall&apos;app, paga online e ritira al bar senza attese. Più tempo
                  per goderti la musica.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center md:justify-start">
                  <a
                    href="https://app.rocknpine.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-rock-yellow text-rock-black font-heading uppercase px-6 py-3 border-4 border-white hover:bg-white transition-all"
                    data-testid="open-web-app-btn"
                  >
                    Web App
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.ebs.gangbandfestival&utm_source=emea_Med"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-rock-black text-white font-heading uppercase px-6 py-3 border-4 border-white hover:bg-rock-yellow hover:text-rock-black transition-all"
                    data-testid="play-store-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M3.609 1.814 13.792 12 3.61 22.186a.998.998 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zM14.499 12.706l2.706 2.708-12.025 6.86 9.319-9.568zm0-1.412L5.18 1.726l12.026 6.86-2.707 2.708zm6.402-2.13 2.402 1.368a1 1 0 0 1 0 1.736l-2.518 1.434-3.087-3.088 3.203-3.45z" />
                    </svg>
                    Play Store
                  </a>
                  <a
                    href="https://apps.apple.com/it/app/gang-band-festival/id6760898881"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-rock-black text-white font-heading uppercase px-6 py-3 border-4 border-white hover:bg-rock-yellow hover:text-rock-black transition-all"
                    data-testid="app-store-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                    App Store
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contatti" data-testid="contact-section" className="py-24 bg-rock-black">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-2">
            CONTATTI
          </h2>
          <div className="h-1 w-32 bg-rock-red mx-auto mb-8" />
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Contact Info */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-rock-red p-3 border-2 border-white">
                  <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-heading text-lg uppercase text-white mb-1">Indirizzo</h4>
                  <p className="font-body text-gray-300">
                    Baselga di Piné (TN)<br />
                    Trentino Alto Adige
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-rock-pink p-3 border-2 border-white">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-heading text-lg uppercase text-white mb-1">Email</h4>
                  <p className="font-body text-gray-300">associazione.rocknpine@gmail.com</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex flex-col justify-center">
              <h4 className="font-heading text-lg uppercase text-white mb-4">Seguici</h4>
              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/RocknPine/"
                  className="bg-rock-zinc border-2 border-rock-border p-3 hover:border-rock-red transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/gangbandfestival.rocknpine/"
                  className="bg-rock-zinc border-2 border-rock-border p-3 hover:border-rock-pink transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-rock-black border-t-4 border-rock-red py-12">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-4">
            <img src={LOGO_MAIN} alt="Rock 'n' Piné" className="h-16 w-auto" />
          </div>

          <div className="text-center md:text-right">
            <p className="font-body text-gray-400 mb-2">
              © 2025 Rock &apos;n Piné APS
            </p>
            <p className="font-body text-gray-500 text-sm">
              Via del Bedolé 8, Baselga di Piné (TN), Italia
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Home Page
const Home = () => {
  // Link diretti a una sezione (es. il QR code della scheda PDF punta a /#affitto-sala):
  // si scorre a pagina caricata, quando le immagini sopra hanno già la loro altezza
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return undefined;
    const scroll = () => document.getElementById(id)?.scrollIntoView();
    if (document.readyState === "complete") {
      scroll();
      return undefined;
    }
    window.addEventListener("load", scroll, { once: true });
    return () => window.removeEventListener("load", scroll);
  }, []);

  return (
    <div className="relative">
      {/* Noise overlay */}
      <div className="noise-overlay" />

      <Navigation />
      <HeroSection />
      <div className="rock-divider" />
      <AboutSection />
      <div className="rock-divider" />
      <FestivalSection />
      <div className="rock-divider" />
      <AppSection />
      <div className="rock-divider" />
      <RehearsalRoomSection />
      <div className="rock-divider" />
      <RentalSection />
      <div className="rock-divider" />
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
